import React from 'react';

interface ProjectDetailsFormProps {
  projectDetails: any;
  setProjectDetails: (details: any) => void;
  selectedPackage: string;
  addons: any;
  timeline: string;
  onNext: () => void;
  onPrev: () => void;
}

const ProjectDetailsForm: React.FC<ProjectDetailsFormProps> = ({
  projectDetails,
  setProjectDetails,
  selectedPackage,
  addons,
  timeline,
  onNext,
  onPrev,
}) => {
  const calculateTotal = () => {
    const basePrice = {
      webapp: 795,
      app: 995,
      both: 1250,
    };

    const addonPrices: { [key: string]: number } = {
      inlogRegistratie: 199,
      androidIOS: 359,
      extraIntegraties: 199,
      seoOptimalisatie: 139,
      googleAds: 139,
      appStorePublicatie: 139,
      betaalIntegratie: 299,
      pushNotificaties: 139,
      pages5to10: 249,
      pages10plus: 449,
      adminDashboard: 249,
      emailFlows: 179,
    };

    // Determine actual package based on buildType selection in Step 2
    let actualPackage: 'webapp' | 'app' | 'both' = selectedPackage as 'webapp' | 'app' | 'both';

    const hasWebapp = projectDetails.buildType.includes('webapp');
    const hasWebsite = projectDetails.buildType.includes('website');
    const hasApp = projectDetails.buildType.includes('app');

    if (hasWebsite && hasApp) {
      actualPackage = 'both';
    } else if (hasApp) {
      actualPackage = 'app';
    } else if (hasWebapp) {
      actualPackage = 'webapp';
    } else if (hasWebsite) {
      // Standalone website is no longer offered, map to combo
      actualPackage = 'both';
    }

    let total = basePrice[actualPackage];

    Object.keys(addons).forEach((key) => {
      if (addons[key as keyof typeof addons]) {
        if (addonPrices[key]) {
          total += addonPrices[key];
        }
      }
    });

    // Calculate days until deadline based on ACTUAL deadline date in Step 2
    if (projectDetails.deadline) {
      const today = new Date();
      today.setHours(0, 0, 0, 0); // Reset time to midnight for accurate day calculation

      const deadlineDate = new Date(projectDetails.deadline);
      deadlineDate.setHours(0, 0, 0, 0);

      const daysUntilDeadline = Math.ceil((deadlineDate.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));

      // Apply pricing based on actual deadline
      if (daysUntilDeadline <= 7) {
        // Super fast: 7 days or less = +50%
        total *= 1.5;
      } else if (daysUntilDeadline <= 14) {
        // 2 weeks: 8-14 days = +25%
        total *= 1.25;
      }
      // 15+ days (1 month) = normal price, no multiplier
    } else {
      // Fallback to timeline if no deadline set yet (shouldn't happen in Step 2)
      if (timeline === 'superfast') {
        total *= 1.5;
      } else if (timeline === '2weeks') {
        total *= 1.25;
      }
    }

    return total;
  };

  const handleInputChange = (field: string, value: any) => {
    setProjectDetails({
      ...projectDetails,
      [field]: value,
    });
  };

  const handleBuildTypeToggle = (type: string) => {
    const newTypes = projectDetails.buildType.includes(type)
      ? projectDetails.buildType.filter((t: string) => t !== type)
      : [...projectDetails.buildType, type];

    handleInputChange('buildType', newTypes);
  };

  return (
    <div>
      {/* Dark Header Section */}
      <div className="bg-black/70 backdrop-blur-sm py-8 mb-12">
        <div className="container mx-auto px-8">
          <div className="max-w-4xl mx-auto">
            {/* Progress Steps */}
            <div className="flex items-center justify-center">
              <div className="flex items-center space-x-2 sm:space-x-4">
                <div className="flex items-center">
                  <div className="text-white w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-sm sm:text-base"
                    style={{
                      background: 'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)',
                    }}>
                    ✓
                  </div>
                  <span className="ml-2 text-white text-sm md:text-base whitespace-nowrap">Stap 1</span>
                </div>

                <div className="w-6 sm:w-12 h-0.5 bg-white/30"></div>

                <div className="flex items-center">
                  <div className="bg-blue-500 text-white w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
                    2
                  </div>
                  <span className="ml-2 text-white text-sm md:text-base whitespace-nowrap">Stap 2</span>
                </div>

                <div className="w-6 sm:w-12 h-0.5 bg-white/30"></div>

                <div className="flex items-center opacity-50">
                  <div className="bg-gray-400 text-white w-8 h-8 sm:w-10 sm:h-10 rounded-full flex items-center justify-center font-bold text-sm sm:text-base">
                    3
                  </div>
                  <span className="ml-2 text-white text-sm md:text-base whitespace-nowrap">Stap 3</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="container mx-auto px-8 pb-16">
        <div className="max-w-4xl mx-auto">
          <div className="bg-white/95 backdrop-blur-md rounded-3xl p-8 shadow-2xl">
          <div className="grid lg:grid-cols-3 gap-8">
            {/* Left side - Form */}
            <div className="lg:col-span-2 space-y-6">
              <h2 className="text-3xl font-bold text-blue-800 mb-2">
                Vertel ons kort over je project
              </h2>
              <p className="text-gray-600 mb-6">
                We hebben enkele projectdetails nodig om je beter te kunnen helpen.
              </p>

              {/* Project Name */}
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Bedrijfsnaam / projectnaam
                </label>
                <input
                  type="text"
                  placeholder="Voer de naam van je bedrijf of project in"
                  value={projectDetails.projectName}
                  onChange={(e) => handleInputChange('projectName', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              </div>

              {/* Build Type */}
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Wat wil je bouwen?
                </label>
                <div className="flex gap-4 flex-wrap">
                  <button
                    onClick={() => handleBuildTypeToggle('webapp')}
                    className={`px-6 py-3 rounded-lg font-semibold transition ${
                      projectDetails.buildType.includes('webapp')
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    🌐 Webapp
                  </button>
                  <button
                    onClick={() => handleBuildTypeToggle('app')}
                    className={`px-6 py-3 rounded-lg font-semibold transition ${
                      projectDetails.buildType.includes('app')
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    📱 App
                  </button>
                  <button
                    onClick={() => handleBuildTypeToggle('website')}
                    className={`px-6 py-3 rounded-lg font-semibold transition ${
                      projectDetails.buildType.includes('website')
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    💻 Website
                  </button>
                  <button
                    onClick={() => handleBuildTypeToggle('anders')}
                    className={`px-6 py-3 rounded-lg font-semibold transition ${
                      projectDetails.buildType.includes('anders')
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Anders
                  </button>
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Korte beschrijving
                </label>
                <textarea
                  placeholder="Omschrijf kort het doel en de functionaliteit van je project"
                  value={projectDetails.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 h-32 resize-none"
                  maxLength={500}
                />
                <p className="text-sm text-gray-500 mt-1">
                  {projectDetails.description.length}/500 characters
                </p>
              </div>

              {/* Has Design */}
              <div>
                <label className="block text-gray-700 font-semibold mb-2">
                  Heb je al design / huisstijl?
                </label>
                <div className="flex gap-4">
                  <button
                    onClick={() => handleInputChange('hasDesign', false)}
                    className={`px-6 py-3 rounded-lg font-semibold transition ${
                      !projectDetails.hasDesign
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Ja
                  </button>
                  <button
                    onClick={() => handleInputChange('hasDesign', true)}
                    className={`px-6 py-3 rounded-lg font-semibold transition ${
                      projectDetails.hasDesign
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-200 text-gray-700 hover:bg-gray-300'
                    }`}
                  >
                    Nee
                  </button>
                </div>
              </div>

            </div>

            {/* Right side - Summary */}
            <div className="bg-blue-50 rounded-xl p-6">
              <h3 className="text-xl font-bold text-blue-800 mb-4">Jouw Pakket</h3>

              <div className="space-y-3 mb-6">
                <div className="flex items-center mb-4">
                  <span className="text-2xl mr-3">📦</span>
                  <span className="font-semibold text-gray-700">
                    {selectedPackage === 'webapp' && 'Webapp'}
                    {selectedPackage === 'app' && 'App'}
                    {selectedPackage === 'both' && 'Website + App'}
                  </span>
                </div>

                {/* Only show extras/add-ons, not base package features */}
                {addons.pages5to10 && (
                  <div className="flex items-center text-gray-600">
                    <svg className="w-4 h-4 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm">Uitbreiding naar 5–10 pagina's</span>
                  </div>
                )}

                {addons.pages10plus && (
                  <div className="flex items-center text-gray-600">
                    <svg className="w-4 h-4 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm">Uitbreiding naar 10+ pagina's</span>
                  </div>
                )}

                {addons.androidIOS && (
                  <div className="flex items-center text-gray-600">
                    <svg className="w-4 h-4 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm">Android & iOS (beide platforms)</span>
                  </div>
                )}

                {addons.appStorePublicatie && (
                  <div className="flex items-center text-gray-600">
                    <svg className="w-4 h-4 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm">App store publicatie</span>
                  </div>
                )}

                {addons.inlogRegistratie && (
                  <div className="flex items-center text-gray-600">
                    <svg className="w-4 h-4 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm">Geavanceerde authenticatie</span>
                  </div>
                )}

                {addons.betaalIntegratie && (
                  <div className="flex items-center text-gray-600">
                    <svg className="w-4 h-4 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm">Betaalintegratie</span>
                  </div>
                )}

                {addons.pushNotificaties && (
                  <div className="flex items-center text-gray-600">
                    <svg className="w-4 h-4 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm">Push notificaties</span>
                  </div>
                )}

                {addons.extraIntegraties && (
                  <div className="flex items-center text-gray-600">
                    <svg className="w-4 h-4 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm">Diverse externe koppelingen</span>
                  </div>
                )}

                {addons.seoOptimalisatie && (
                  <div className="flex items-center text-gray-600">
                    <svg className="w-4 h-4 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm">Basis SEO-optimalisatie</span>
                  </div>
                )}

                {addons.googleAds && (
                  <div className="flex items-center text-gray-600">
                    <svg className="w-4 h-4 text-orange-500 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                    </svg>
                    <span className="text-sm">Integratie met advertentieplatformen</span>
                  </div>
                )}
              </div>

              <div className="border-t pt-4">
                <div className="flex justify-between items-center mb-4">
                  <span className="text-lg font-bold text-gray-700">Totaal:</span>
                  <span className="text-2xl font-bold text-blue-600">€ {calculateTotal().toLocaleString('nl-NL')},-</span>
                </div>

                <button
                  onClick={onNext}
                  className="w-full text-white py-3 rounded-lg font-semibold transition flex items-center justify-center transform hover:scale-105"
                  style={{
                    background: 'radial-gradient(circle at center, #ff8a3d 0%, #f06c28 45%, #d4551b 100%)',
                  }}
                >
                  Ga verder
                  <svg className="w-5 h-5 ml-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Navigation */}
          <div className="mt-8 flex justify-between">
            <button
              onClick={onPrev}
              className="text-blue-600 hover:text-blue-700 font-semibold flex items-center"
            >
              <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
              </svg>
              Vorige stap
            </button>
          </div>
        </div>
          </div>
      </div>
    </div>
  );
};

export default ProjectDetailsForm;